import React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { Attachment } from './types';

export type AttachmentChipStatus = 'ready' | 'uploading' | 'error';

interface Props {
  attachment: Attachment;
  onRemove: () => void;
  disabled?: boolean;
  /**
   * Per-chip status. Default `'ready'` so existing call sites are
   * unaffected. `'uploading'` shows an inline spinner; `'error'` shows
   * an error icon and (when `onRetry` is provided) a retry button.
   */
  status?: AttachmentChipStatus;
  /** Optional message surfaced via tooltip on the error icon. */
  errorMessage?: string;
  /** Only rendered when `status === 'error'`. */
  onRetry?: () => void;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const AttachmentChip: React.FC<Props> = ({
  attachment,
  onRemove,
  disabled,
  status = 'ready',
  errorMessage,
  onRetry,
}) => {
  // Choose the leading icon: spinner during upload, file icon otherwise.
  // We keep overall chip dimensions consistent by using the same
  // ~14px sizing for the spinner.
  const leadingIcon =
    status === 'uploading' ? (
      <CircularProgress
        size={14}
        thickness={5}
        // Inherit chip color so contrast works on the green-tinted bg.
        sx={{ color: 'inherit' }}
      />
    ) : (
      <AttachFileIcon fontSize="inherit" />
    );

  const errorIcon =
    status === 'error' ? (
      <Tooltip title={errorMessage || ''} disableHoverListener={!errorMessage}>
        <ErrorOutlineIcon
          color="error"
          fontSize="inherit"
          data-testid="attachment-chip-error-icon"
          aria-label={errorMessage || undefined}
          titleAccess={errorMessage || undefined}
        />
      </Tooltip>
    ) : null;

  const retryButton =
    status === 'error' && onRetry ? (
      <IconButton
        aria-label="Retry upload"
        size="small"
        disabled={disabled}
        onClick={onRetry}
        sx={{ p: 0.25, color: 'inherit' }}
      >
        <RefreshIcon fontSize="inherit" />
      </IconButton>
    ) : null;

  return (
    <Box
      data-filename={attachment.filename}
      data-status={status}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 16,
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
        color: (theme) => theme.palette.mode === 'dark'
          ? theme.palette.success.main
          : theme.palette.success.dark,
        maxWidth: '100%',
        fontSize: '0.75rem',
      }}
    >
      {leadingIcon}
      <Typography
        component="span"
        variant="caption"
        sx={{
          maxWidth: 160,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={`${attachment.filename} (${humanSize(attachment.size)})`}
      >
        {attachment.filename}
      </Typography>
      {errorIcon}
      {retryButton}
      <IconButton
        aria-label={`Remove ${attachment.filename}`}
        size="small"
        disabled={disabled}
        onClick={onRemove}
        sx={{ p: 0.25, color: 'inherit' }}
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
    </Box>
  );
};

export default AttachmentChip;
